import { toRefs, ref, computed } from '@vue/composition-api'
import { judgeType, interpretValue } from '@/utils/typeUtils'
import type {
  AggregationResultAttributes,
  BusinessRuleResource,
  ResultResource,
  RangeDailyValue,
  ContextResource,
  ContextType,
  BRDisplayType,
} from '@/api/v2/types/aggregation-result'
import { useAggregationType } from './useAggregationType'

export type DisplayMode = 'daily' | 'sum' | 'today'

type Header = {
  text: string,
  value: BusinessRuleResource['id'] | string,
  minWidth?: string,
  maxWidth?: string,
  width?: string,
  class?: string,
  cellClass?: string,
}
type BusinessRuleHeader = Omit<Header, 'value'> & {
  value: BusinessRuleResource['id']
}
// Itemがデータ行
type ResultValue = number | string | boolean
type BusinessRuleId = string
type ItemMeta = {
  _inputTypes: Record<BusinessRuleId, string>
  _originalValues: Record<BusinessRuleId, ResultValue>
  businessRuleResultIds: Record<BusinessRuleId, number | null>
  dependentBusinessRuleIds: Record<BusinessRuleId, Array<BusinessRuleId>>
  dependentResources: Record<BusinessRuleId, any>
  editable: Record<BusinessRuleId, boolean>
  inputTypes: Record<BusinessRuleId, string>
  originalValues: Record<BusinessRuleId, ResultValue>
}
type BaseItem = {
  id: number
  contextType: string
  name: string
  meta: ItemMeta
  [businessRuleId: string]: ResultValue | ItemMeta
}
type DailyItem = BaseItem & { date: string }
type SumItem = BaseItem

type AggregationResultTableProps = {
  aggregation: AggregationResultAttributes;
  loading: boolean;
  displayMode: DisplayMode;
  today: string;
  brDisplaySettings: Record<string, BRDisplayType>;
  hideZeroRows: boolean;
}

type Acc = number | string

const toNumber = (v: any): number => {
  if (typeof v === 'number') return v
  if (typeof v === 'boolean') return v ? 1 : 0
  const parsed = parseFloat(String(v))

  return Number.isNaN(parsed) ? 0 : parsed
}
const isTime = (s: string) => /^\d{1,2}:[0-5]\d$/.test(s)
const toMinutes = (s: string) => {
  const [h, m] = s.split(':').map(Number)

  return h * 60 + m
}
const fmtHHMM = (mins: number) => {
  const h = Math.floor(mins / 60)
  const m = mins % 60

  return `${h}:${String(m).padStart(2, '0')}`
}
export const sumValues = (vals: { value: any }[]): Acc => {
  return vals.reduce<Acc>((acc, cur) => {
    const raw = cur?.value
    // すでに「非時間の文字列」でロック済みなら、そのまま
    if (typeof acc === 'string' && !isTime(acc)) return acc

    if (typeof raw === 'string') {
      if (isTime(raw)) {
        // 時間文字列 → 時間合算
        const baseMins = typeof acc === 'string' ? toMinutes(acc) : (acc as number)

        return fmtHHMM(baseMins + toMinutes(raw))
      }

      return raw // 非時間の文字列 → ロック
    }

    // 数値/真偽値/その他数値化できるものは分として加算
    const add = toNumber(raw)

    // 既に時間モードなら HH:MM で返す
    if (typeof acc === 'string') {
      const baseMins = toMinutes(acc) // acc は時間文字列であることが保証されている

      return fmtHHMM(baseMins + add)
    }

    // まだ数値モードなら数値で積み上げ
    return (acc as number) + add
  }, 0)
}

// 非出勤日など「データなし」を意味する falsy 値（null / undefined / '' / 0 / false）を除外し、
// 期間中の重複を除去した上で最後に初登場した値を返す。
export const uniqueValues = (vals: { value: any }[]): any => {
  const seen: Record<string, boolean> = {}
  let lastUniqueSeen: any = null

  vals.forEach(item => {
    const v = item?.value
    if (v === null || v === undefined || v === '' || v === 0 || v === false) return
    const key = String(v)
    if (!seen[key]) {
      seen[key] = true
      lastUniqueSeen = v
    }
  })

  return lastUniqueSeen
}

export const maxValue = (vals: { value: any }[]): Acc => {
  const nonNull = vals.filter(v => v.value !== null && v.value !== undefined && v.value !== '')
  if (nonNull.length === 0) return 0

  const firstRaw = nonNull[0].value
  if (typeof firstRaw === 'string' && isTime(firstRaw)) {
    const maxMins = nonNull.reduce((acc, cur) => {
      if (typeof cur.value === 'string' && isTime(cur.value)) {
        return Math.max(acc, toMinutes(cur.value))
      }

      return acc
    }, 0)

    return fmtHHMM(maxMins)
  }

  return nonNull.reduce((acc, cur) => {
    const n = toNumber(cur.value)

    return n > (acc as number) ? n : acc
  }, toNumber(nonNull[0].value))
}

export const lastValue = (vals: { value: any }[]): any => {
  const nonNull = vals.filter(v => v.value !== null && v.value !== undefined && v.value !== '')

  return nonNull.length > 0 ? nonNull[nonNull.length - 1].value : null
}

// 分母: businessRuleResultId が非null（DBに保存済み）の件数を営業日としてカウント
export const avgValue = (vals: { value: any; businessRuleResultId?: number | null }[]): Acc => {
  const saved = vals.filter(v => v.businessRuleResultId != null)
  if (saved.length === 0) return 0

  const firstRaw = saved[0].value
  if (typeof firstRaw === 'string' && isTime(firstRaw)) {
    const totalMins = saved.reduce((acc, cur) => {
      if (typeof cur.value === 'string' && isTime(cur.value)) {
        return acc + toMinutes(cur.value)
      }

      return acc
    }, 0)

    return fmtHHMM(Math.round(totalMins / saved.length))
  }

  const total = saved.reduce((acc, cur) => acc + toNumber(cur.value), 0)

  return Math.round((total / saved.length) * 10) / 10
}

const computeBRValue = (vals: { value: any }[], type: BRDisplayType): Acc => {
  switch (type) {
    case 'unique': return uniqueValues(vals) ?? 0
    case 'max': return maxValue(vals)
    case 'last': return lastValue(vals) ?? 0
    case 'avg': return avgValue(vals)
    case 'sum':
    default: return sumValues(vals)
  }
}

export function useAggregationResultTable(
  props: AggregationResultTableProps,
  emit: any,
) {
  const {
    aggregation, loading, displayMode, today, brDisplaySettings, hideZeroRows,
  } = toRefs(props)
  const justSaved = ref(false)
  const { isUserAggregation } = useAggregationType(aggregation.value)

  // 共通：ヘッダー構築
  const businessRuleHeaders = computed<BusinessRuleHeader[]>(() => {
    const rules = aggregation.value.businessRules.data
    const filtered = displayMode.value === 'sum'
      ? rules.filter(r => (brDisplaySettings.value[r.id] || 'sum') !== 'hidden')
      : rules

    return filtered.map(r => ({
      text: r.attributes.name,
      value: r.id,
      maxWidth: '160px',
      minWidth: '160px',
      width: '160px',
    }))
  })

  const headers = computed<Header[]>(() => {
    const left: Header[] = []
    if (displayMode.value === 'daily' || displayMode.value === 'today') {
      left.push({
        text: '日付',
        value: 'date',
        class: 'fixed-cell',
        cellClass: 'fixed-cell',
        maxWidth: '160px',
        minWidth: '160px',
        width: '160px',
      })
    } else if (displayMode.value === 'sum' && isUserAggregation.value) {
      left.push({
        text: 'ユーザー',
        value: 'name',
        class: 'fixed-cell',
        cellClass: 'fixed-cell',
        maxWidth: '160px',
        minWidth: '160px',
        width: '160px',
      })
    }

    return left.concat(businessRuleHeaders.value)
  })

  const items = computed(() => (contextType: ContextType | null = null, contextId: number | null = null) => {
    const all: Array<SumItem | DailyItem> = []
    let contexts = aggregation.value.contexts.data
    if (contextType && contextId) {
      contexts = contexts.filter(ctx => ctx.attributes.contextType === contextType && ctx.attributes.id === contextId)
    }

    contexts.forEach((ctxRes: ContextResource) => {
      const {
        id, contextType: ctxType, name, results,
      } = ctxRes.attributes
      const byRule: ResultResource[] = results.data
      const makeBase = (): BaseItem => ({
        id,
        contextType: ctxType,
        name,
        meta: {
          _originalValues: {},
          _inputTypes: {},
          businessRuleResultIds: {},
          dependentBusinessRuleIds: {},
          dependentResources: {},
          editable: {},
          get originalValues() { return { ...this._originalValues } },
          get inputTypes() { return { ...this._inputTypes } },
        },
      })

      if (displayMode.value === 'sum') {
        // 各ルールID をタイプに応じて計算
        const row = makeBase() as SumItem
        byRule.forEach(r => {
          const brId = r.attributes.businessRuleId
          const displayType: BRDisplayType = brDisplaySettings.value[brId] || 'sum'
          if (displayType === 'hidden') return

          const vals = r.attributes.value as RangeDailyValue[]
          const value = computeBRValue(vals, displayType)
          row[brId] = value

          // meta 情報もセット
          row.meta._originalValues[brId] = value
          row.meta._inputTypes[brId] = typeof value === 'string' ? 'string' : 'number'
          row.meta.businessRuleResultIds[brId] = r.attributes.businessRuleResultId ?? null
          row.meta.dependentResources[brId] = r.attributes.dependentResources
          row.meta.dependentBusinessRuleIds[brId] = r.attributes.dependentBusinessRuleIds || []
          row.meta.editable[brId] = false
        })
        all.push(row)
      } else if (displayMode.value === 'daily' || displayMode.value === 'today') {
        // DailyItem を日付×コンテキストごとに作成
        const mapByDate: Record<string, DailyItem> = {}
        byRule.forEach(r => {
          const brId = r.attributes.businessRuleId
          const vals = r.attributes.value as RangeDailyValue[]
          vals.forEach(v => {
            // 当日表示の場合は当日のデータのみを表示
            if (displayMode.value === 'today' && !!today.value && v.date !== today.value) {
              return
            }

            // 初回なら雛形＋date をセット
            if (!mapByDate[v.date]) {
              const base = makeBase() as DailyItem
              base.date = v.date
              mapByDate[v.date] = base
            }
            const item = mapByDate[v.date]
            const val = interpretValue(v.value)

            // 値セット
            item[brId] = val

            // meta 情報セット
            item.meta._originalValues[brId] = val
            item.meta._inputTypes[brId] = judgeType(v.value)
            item.meta.businessRuleResultIds[brId] = v.businessRuleResultId ?? r.attributes.businessRuleResultId ?? null
            item.meta.dependentResources[brId] = r.attributes.dependentResources
            item.meta.dependentBusinessRuleIds[brId] = r.attributes.dependentBusinessRuleIds || []
            item.meta.editable[brId] = r.attributes.editable
          })
        })

        // mapの値を配列化して push
        Object.values(mapByDate).forEach(it => all.push(it))
      }
    })

    // 全BR列が0/null/undefinedの行を非表示
    if (hideZeroRows.value && (displayMode.value === 'daily' || displayMode.value === 'sum')) {
      return all.filter(item => businessRuleHeaders.value.some(h => {
        const v = item[h.value]

        return v !== 0 && v !== null && v !== undefined
      }))
    }

    return all
  })

  const aggregationTable = computed(() => (contextType: ContextType | null = null, contextId: number | null = null) => {
    const {
      id, name, description, type,
    } = aggregation.value

    return {
      id,
      name,
      description,
      businessRuleHeaders: businessRuleHeaders.value,
      headers: headers.value,
      items: items.value(contextType, contextId),
      type,
    }
  })

  const saveResultValue = (
    item: DailyItem,
    businessRuleId: BusinessRuleResource['id'],
    resultValue: string | number | boolean,
    menu: { value: boolean },
  ) => {
    justSaved.value = true
    // eslint-disable-next-line no-param-reassign
    menu.value = false

    const { id: contextId, contextType, date } = item
    emit('save', {
      aggregationId: aggregation.value.id,
      contextId,
      contextType,
      businessRuleId,
      resultValue,
      businessDate: date,
    })
  }

  const onMenuInput = (open: boolean, item: DailyItem, businessRuleId: BusinessRuleHeader['value']) => {
    // 保存時に閉じた時は何もしない
    if (!open && justSaved.value) {
      justSaved.value = false

      return
    }

    // escや外側クリックなどで編集モードを抜ける場合は元に戻す
    if (!open) {
      // eslint-disable-next-line no-param-reassign
      item[businessRuleId] = item.meta.originalValues[businessRuleId]
    }
  }

  const isBooleanItem = computed(() => (item: BaseItem, brId: BusinessRuleHeader['value']) => item.meta.inputTypes[brId] === 'boolean')
  const isNumberItem = computed(() => (item: BaseItem, brId: BusinessRuleHeader['value']) => item.meta.inputTypes[brId] === 'number')
  const isNew = computed(() => (item: BaseItem, brId: BusinessRuleHeader['value']) => {
    // 合計表示は編集できいないので, 未保存とか関係ない
    if (displayMode.value === 'sum') return false

    return !item.meta.businessRuleResultIds[brId]
  })
  const isNotEditable = computed(() => (item: BaseItem, brId: BusinessRuleHeader['value']) => {
    // 合計表示は編集させない
    if (displayMode.value === 'sum') return true

    return loading.value || !item.meta.editable[brId]
  })
  const inputType = computed(() => (item: BaseItem, brId: BusinessRuleHeader['value']) => item.meta.inputTypes[brId])
  const numberText = computed(() => (item: DailyItem | SumItem, brId: BusinessRuleHeader['value']) => {
    const val = item[brId]
    if (val === null || val === undefined) return ''

    return (val as number | string).toLocaleString('ja-JP')
  })

  // 合計行の計算
  const accRow = computed(() => (contextType: ContextType | null = null, contextId: number | null = null) => {
    const sums: Record<string, any> = {}
    const itemsData = items.value(contextType, contextId)

    headers.value.forEach(header => {
      if (header.value === 'date' || header.value === 'name') {
        sums[header.value] = '合計'
      } else {
        const total = itemsData.reduce((acc, item) => {
          const val = item[header.value]

          // 数値として変換可能な値を処理
          if (val === null || val === undefined || val === '') {
            return acc
          }

          const numVal = Number(val)

          return Number.isNaN(numVal) ? acc : acc + numVal
        }, 0)

        // 0の場合も表示
        sums[header.value] = total
      }
    })

    return sums
  })

  return {
    aggregationTable,
    isBooleanItem,
    isNumberItem,
    isNew,
    isNotEditable,
    inputType,
    numberText,
    saveResultValue,
    onMenuInput,
    accRow,
  }
}
